import React, { useState, useEffect } from 'react';
import { useAdminStore } from '../../../../store/adminStore';
import { api } from '../../../../services/apiServices';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Loader2, Save, Image as ImageIcon, MapPin, Globe } from 'lucide-react';

// Reuse existing components
import LocationSearch from '../../../../components/Admin/SEO/LocationSearch';
import ImageUploaderComponent from '../../../../components/ImageUploader';
import { uploadFileToS3 } from '../../../../utility/S3Uploader';
import { extractS3Path } from '../../../../utility/stripS3Prefix';
import MediaLibraryModal from '../../../../components/MediaLibraryModal';

const LocationImageManager = () => {
  const { locale, countries, setLocale, setCountries } = useAdminStore((state) => state);
  
  // UI State
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  // Form State (Matches DB Columns)
  const [formData, setFormData] = useState({
    hero_image: '',
    hero_image_alt: ''
  });

  // Init
  useEffect(() => {
    if (countries.length === 0) {
        api.get('/admin/common/active-countries')
          .then(res => {
             setCountries(res.data || []);
             if (!locale && res.data && res.data.length > 0) setLocale(res.data[0].country_code);
          })
          .catch(err => console.error("Failed to fetch countries:", err));
    }
  }, [countries.length, setCountries, locale, setLocale]);

  // Handlers
  const handleLocationSelect = async (loc) => {
    if (!loc) {
        setSelectedLocation(null);
        return;
    }
    
    setLoading(true);
    try {
        // Fetch full details
        const res = await api.get(`/admin/seo/locations/${loc.id}`);
        setSelectedLocation(res.data);
        // Map backend response to form
        setFormData({
            hero_image: res.data.hero_image || '',
            hero_image_alt: res.data.hero_image_alt || ''
        });
    } catch (err) {
        console.error("Failed to fetch location details", err);
        alert("Error loading location data");
    } finally {
        setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    const timestamp = Date.now();
    const fileExt = file.name.split('.').pop();
    const fileNameBase = file.name.substring(0, file.name.lastIndexOf('.'));
    const sanitizedBase = fileNameBase.replace(/[^a-zA-Z0-9-_]/g, '-').toLowerCase();
    const newFileName = `${sanitizedBase}-${timestamp}.${fileExt}`;

    const renamedFile = new File([file], newFileName, { type: file.type });
    try {
        const url = await uploadFileToS3(renamedFile, "public/seo/locations");
        setFormData(prev => ({ ...prev, hero_image: url }));
    } catch (e) { alert("Upload failed"); }
  };

  const handleLibrarySelect = (url) => {
    setFormData(prev => ({ ...prev, hero_image: extractS3Path(url) }));
    setIsLibraryOpen(false);
  };

  const handleSave = async () => {
      if (!selectedLocation) return;
      setSaving(true);
      try {
          await api.put(`/admin/seo/locations/${selectedLocation.id}/image`, formData);
          alert(`Saved image for ${selectedLocation.city}!`);
      } catch (err) {
          alert("Failed to save.");
          console.error(err);
      } finally {
          setSaving(false);
      }
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50/50 h-full flex flex-col">
      
      {/* HEADER */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg border shadow-sm">
        <div className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-blue-600" />
            <div>
                <h1 className="text-xl font-bold text-gray-800">Location Images</h1>
                <p className="text-xs text-gray-500">Set hero images for specific cities/locations.</p>
            </div>
        </div>

        {/* Country Selector */}
        <div className="flex items-center gap-3 bg-blue-50 px-4 py-2 rounded-md border border-blue-100">
            <Globe className="w-4 h-4 text-blue-600" />
            <Select value={locale || ''} onValueChange={(val) => {
                setLocale(val);
                setSelectedLocation(null);
            }}>
                <SelectTrigger className="w-[140px] h-8 border-none bg-transparent focus:ring-0 p-0 text-sm font-medium">
                    <SelectValue placeholder="Country" />
                </SelectTrigger>
                <SelectContent>
                    {countries.map((c) => (
                        <SelectItem key={c.country_code} value={c.country_code}>{c.name}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 flex-1">
          
          {/* LEFT: Search Panel */}
          <div className="md:col-span-1 bg-white p-6 rounded-lg border shadow-sm h-fit">
              <Label className="mb-2 block text-base font-semibold">1. Search Location</Label>
              <div className="p-1 border rounded bg-gray-50 mb-4">
                  <LocationSearch 
                    countryCode={locale} 
                    onSelect={handleLocationSelect}
                    placeholder={`City in ${locale}...`}
                  />
              </div>
              
              {!selectedLocation && (
                  <div className="text-center py-10 text-gray-400 border-2 border-dashed rounded-lg">
                      <MapPin className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Select a city to edit</p>
                  </div>
              )}
          </div>

          {/* RIGHT: Editor Panel */}
          {selectedLocation && (
             <div className="md:col-span-2 bg-white p-6 rounded-lg border shadow-sm animate-in fade-in slide-in-from-right-4">
                <div className="flex justify-between items-center border-b pb-4 mb-6">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-green-600"/>
                            {selectedLocation.city}
                        </h2>
                        <p className="text-xs text-gray-500">{selectedLocation.state_code}, {selectedLocation.country_code}</p>
                    </div>
                    {loading && <Loader2 className="animate-spin text-blue-600" />}
                </div>

                <div className="space-y-6 max-w-xl">
                    {/* Image Upload */}
                    <div>
                        <Label className="mb-2 block">Hero Image</Label>
                        <div className="flex gap-4 items-start">
                            <div className="flex-1">
                                <ImageUploaderComponent 
                                    onChange={handleImageUpload}
                                    maxFiles={1}
                                    initialUrls={formData.hero_image ? [formData.hero_image] : []}
                                    onBrowse={() => setIsLibraryOpen(true)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Alt Text */}
                    <div>
                        <Label className="mb-1 block">Image Alt Text (SEO)</Label>
                        <Input 
                            value={formData.hero_image_alt} 
                            onChange={(e) => setFormData(p => ({...p, hero_image_alt: e.target.value}))}
                            placeholder={`e.g. Skyline view of ${selectedLocation.city}`}
                        />
                    </div>

                    {/* Save Button */}
                    <div className="pt-4 border-t">
                        <Button onClick={handleSave} disabled={saving} className="w-full md:w-auto min-w-[150px]">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Save className="w-4 h-4 mr-2"/>}
                            Save Changes
                        </Button>
                    </div>
                </div>
             </div>
          )}
      </div>

      <MediaLibraryModal 
        open={isLibraryOpen} 
        onOpenChange={setIsLibraryOpen} 
        onSelect={handleLibrarySelect} 
      />
    </div>
  );
};

export default LocationImageManager;